import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FunctionRecord, PackageFormValues, PackageStatus } from '../types';
import { PackageFunctionsList } from './PackageFunctionsList';
import './PackageFormModal.scss';

const formSchema = z.object({
  name: z.string().min(2, 'Tên gói tối thiểu 2 ký tự'),
  code: z.string().min(2, 'Mã gói tối thiểu 2 ký tự'),
  origin_price: z.number().min(0, 'Giá gốc phải lớn hơn 0'),
  sale_price: z.number().min(0, 'Giá bán phải lớn hơn 0'),
  position: z.number().optional().nullable(),
  description: z.string().optional().nullable(),
  status: z.enum(['active', 'inactive'] as [PackageStatus, PackageStatus]).optional(),
  function_ids: z.array(z.string()),
});

interface PackageFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues?: Partial<PackageFormValues>;
  functions: FunctionRecord[];
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (values: PackageFormValues) => void;
}

export function PackageFormModal({
  open,
  mode,
  initialValues,
  functions,
  submitting,
  onClose,
  onSubmit,
}: PackageFormModalProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PackageFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      code: initialValues?.code ?? '',
      origin_price: initialValues?.origin_price ?? 0,
      sale_price: initialValues?.sale_price ?? 0,
      position: initialValues?.position ?? undefined,
      description: initialValues?.description ?? '',
      status: initialValues?.status ?? 'active',
      function_ids: initialValues?.function_ids ?? [],
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: initialValues?.name ?? '',
        code: initialValues?.code ?? '',
        origin_price: initialValues?.origin_price ?? 0,
        sale_price: initialValues?.sale_price ?? 0,
        position: initialValues?.position ?? undefined,
        description: initialValues?.description ?? '',
        status: initialValues?.status ?? 'active',
        function_ids: initialValues?.function_ids ?? [],
      });
    }
  }, [initialValues, open, reset]);

  const selectedFunctionIds = watch('function_ids');

  const submitHandler = handleSubmit((values) => {
    onSubmit({
      ...values,
      position: values.position ?? undefined,
      description: values.description ?? undefined,
    });
  });

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  if (!open) return null;

  return (
    <div className="hrm-modal-overlay">
      <div className="hrm-modal" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-xl">
          <div className="hrm-modal-content modal-content package-form-modal">
          <div className="modal-header package-form-modal__header">
            <div className="header-stack">
              <span className="header-icon">
                <i className="ti ti-packages" />
              </span>
              <div>
                <h5 className="modal-title mb-1">
                  {mode === 'create' ? 'Tạo gói dịch vụ mới' : 'Chỉnh sửa gói dịch vụ'}
                </h5>
                <p className="header-subtitle mb-0">Tối ưu giá, trạng thái và chức năng cho từng gói SaaS.</p>
              </div>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <form onSubmit={submitHandler}>
            <div className="modal-body">
              <div className="form-section">
                <div className="section-heading">
                  <span className="section-icon">
                    <i className="ti ti-info-circle" />
                  </span>
                  <div>
                    <p className="section-label">Thông tin cơ bản</p>
                    <h6>Định danh và mô tả gói dịch vụ</h6>
                  </div>
                </div>
                <div className="form-grid form-grid--two">
                    <div className="form-field">
                      <label className="form-label required">Tên gói</label>
                      <input
                        className="form-control"
                        placeholder="VD: Business Pro"
                        {...register('name')}
                      />
                      {errors.name && <span className="text-danger small">{errors.name.message}</span>}
                    </div>
                    <div className="form-field">
                      <label className="form-label required">Mã gói</label>
                      <input
                        className="form-control"
                        placeholder="VD: BUSINESS_PRO"
                        {...register('code')}
                        disabled={mode === 'edit'}
                      />
                      {errors.code && <span className="text-danger small">{errors.code.message}</span>}
                    </div>
                    <div className="form-field form-field--full">
                      <label className="form-label">Mô tả</label>
                      <textarea
                        rows={3}
                        className="form-control"
                        {...register('description')}
                        placeholder="Tóm tắt lợi ích, chính sách áp dụng…"
                      />
                    </div>
                  </div>
              </div>
              <div className="form-section">
                <div className="section-heading">
                  <span className="section-icon">
                    <i className="ti ti-adjustments-alt" />
                  </span>
                  <div>
                    <p className="section-label">Cấu hình thêm</p>
                    <h6>Giá bán, vị trí hiển thị và trạng thái</h6>
                  </div>
                </div>
                <div className="form-grid form-grid--three">
                    <div className="form-field">
                      <label className="form-label required">Giá gốc (VND)</label>
                      <input
                        type="number"
                        min={0}
                        step="10000"
                        className="form-control"
                        {...register('origin_price', { valueAsNumber: true })}
                      />
                      {errors.origin_price && (
                        <span className="text-danger small">{errors.origin_price.message}</span>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label required">Giá bán (VND)</label>
                      <input
                        type="number"
                        min={0}
                        step="10000"
                        className="form-control"
                        {...register('sale_price', { valueAsNumber: true })}
                      />
                      {errors.sale_price && (
                        <span className="text-danger small">{errors.sale_price.message}</span>
                      )}
                    </div>
                    <div className="form-field">
                      <label className="form-label">Vị trí hiển thị</label>
                      <input
                        type="number"
                        className="form-control"
                        {...register('position', { valueAsNumber: true })}
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Trạng thái</label>
                      <select className="form-select" {...register('status')}>
                        <option value="active">Hoạt động</option>
                        <option value="inactive">Vô hiệu hóa</option>
                      </select>
                    </div>
                  </div>
              </div>
              <div className="form-section">
                <div className="section-heading">
                  <span className="section-icon">
                    <i className="ti ti-apps" />
                  </span>
                  <div>
                    <p className="section-label">Chức năng trong gói</p>
                    <h6>Chọn chức năng kèm theo từng gói</h6>
                  </div>
                </div>
                <PackageFunctionsList
                  functions={functions}
                  selectedIds={selectedFunctionIds}
                  onChange={(ids) => setValue('function_ids', ids)}
                />
                {errors.function_ids && (
                  <span className="text-danger small d-block mt-2">{errors.function_ids.message}</span>
                )}
              </div>
            </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                  Hủy
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Đang lưu…' : 'Lưu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}


